import * as React from "react"
import { MdArrowDownward } from "react-icons/md"
import { ThemeCss, Spacer, InputFieldBlock, Button } from "gatsby-interface"
import { NarrowSingleColumn } from "@modules/ui/layouts/Containers"
import { useOrganizationNameFieldsFragment } from "@modules/organization/Settings/fragments.generated"
import {
  ErrorAlert,
  useTriggerErrorAlert,
} from "@modules/ui/components/ErrorAlert"
import {
  useSourceProviderTeamsForUserQuery,
  useBranchesForSourceRepositoryLazyQuery,
} from "@modules/site/create/Import/queries.generated"
import {
  SourceControlProvider,
  SourceOrganization,
  Repository,
} from "@modules/graphql/types"
import Loading from "@modules/ui/components/Loading"
import useInstallationWindow from "@modules/toolkit/closable-window/useInstallationWindow"
import { useValidateUniqueSiteName } from "@modules/site/shared/hooks/useValidateUniqueSiteName"
import { StyledForm } from "@modules/ui/components/Primitives"
import { useCreateSite } from "@modules/onboarding/gaFlow/hooks/useCreateSite"
import { useRepositoriesForInstallation } from "@modules/site/create/Import/hooks/useRepositoriesForInstallation"
import { BranchField } from "@modules/onboarding/createFromExisting/components/PickRepository.fields"
import {
  getCurrentSourceProviderTeam,
  useRepoForm,
  RepoFormActionType,
  getUniqueSiteName,
  getDefaultSiteName,
} from "@modules/site/create/Import/components/PickRepository.helpers"
import { useTracker } from "@modules/analytics"
import {
  importSite as importSiteText,
  importSiteNew as newImportSiteText,
  ui as uiText,
} from "@modules/locales/default.js"
import { NoInstallations } from "@modules/site/create/shared/components/NoInstallations"

const DEFAULT_BRANCHES = [`master`, `main`, `default`]

function isDefaultBranch(branch: string) {
  return DEFAULT_BRANCHES.includes(branch)
}

const formSectionCss: ThemeCss = theme => ({
  display: "flex",
  width: "100%",
  flexWrap: "wrap",
  flexDirection: "column",
  marginBottom: theme.space[9],
  [theme.mediaQueries.tablet]: {
    flexWrap: "nowrap",
    flexDirection: "row",
  },
})

type FieldWrapperProps = {
  size?: number
  withLeftSpacing?: boolean
  children?: React.ReactNode
}
function FieldWrapper({ size, withLeftSpacing, children }: FieldWrapperProps) {
  return (
    <div
      css={theme => ({
        flex: size || 1,
        [theme.mediaQueries.tablet]: {
          marginLeft: withLeftSpacing ? theme.space[7] : 0,
        },
      })}
    >
      {children}
    </div>
  )
}

export type BasicConfigurationProps = {
  installationId: string
  organizationId: string
  vcsConfigId?: string
  repository: string
  provider: SourceControlProvider
  onSiteCreated: (siteId: string) => void
  isCurrentStep: boolean
}

export default function BasicConfiguration({
  installationId,
  organizationId,
  vcsConfigId,
  repository: repoName,
  provider,
  onSiteCreated,
  isCurrentStep,
}: BasicConfigurationProps) {
  // Initialize state
  const [setError, errorAlert] = useTriggerErrorAlert()
  const [hasClosedWindow, setHasClosedWindow] = React.useState(false)
  const [formState, dispatch] = useRepoForm({ installation: installationId })

  // Get functions for creating the site
  const [createSite, createSiteLoading] = useCreateSite({ setError })
  const [
    verifySiteNameUniqueness,
    {
      loading: loadingSiteNameUniqueness,
      error: serverError,
      validationError,
      clearError,
    },
  ] = useValidateUniqueSiteName()

  // Hook for tracking function
  const { trackAction } = useTracker()

  // Hook for installing new organization if one not available
  const { popInstallationWindow } = useInstallationWindow(
    (installationId: string) => {
      refetch()

      if (installationId) {
        dispatch({
          type: RepoFormActionType.SelectInstallation,
          payload: installationId,
        })
      }

      setHasClosedWindow(true)
    },
    provider === SourceControlProvider.GithubEnterprise
      ? { vcsConfigId }
      : undefined
  )

  // Get organizations for given git provider
  const { data, loading, error, refetch } = useSourceProviderTeamsForUserQuery({
    variables: {
      provider,
      ...(vcsConfigId && { vcsConfigId }),
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  })

  const sourceOrganizations = (data && data.sourceProviderTeamsForUser) || []

  const { data: orgNameData } = useOrganizationNameFieldsFragment(
    organizationId
  )

  const selectedInstallation = getCurrentSourceProviderTeam(
    formState.installation,
    orgNameData?.name,
    sourceOrganizations as SourceOrganization[]
  )

  // Get repositories for a given git organization (installationId)
  const [repositories, { error: errorRepos }] = useRepositoriesForInstallation(
    {
      installationId: selectedInstallation?.id || "",
      provider,
      vcsConfigId,
      isUserTeam: selectedInstallation?.isUserTeam,
    },
    () => undefined
  )

  if (errorRepos) {
    setError(errorRepos)
  }

  // Select the repository that was passed in by query param
  const repository = repositories.find(repo => repo.name === repoName)

  // Get function to load branches for given repository
  const [loadBranches] = useBranchesForSourceRepositoryLazyQuery({
    onCompleted: async data => {
      const branches = data?.branchesForSourceRepository.branches ?? []
      const defaultBranch = branches.find(isDefaultBranch) ?? branches[0]

      let suggestedSiteName = getDefaultSiteName(repoName, defaultBranch)
      const isUniqueName = await verifySiteNameUniqueness({
        name: suggestedSiteName,
        sourceOrganizationId: selectedInstallation?.id,
        organizationId: organizationId,
        sourceControlProvider: provider,
      })
      clearError()

      if (!isUniqueName) {
        suggestedSiteName = getUniqueSiteName(suggestedSiteName)
      }

      dispatch({
        type: RepoFormActionType.InitializeBranch,
        payload: { branch: defaultBranch ?? ``, siteName: suggestedSiteName },
      })
    },
    fetchPolicy: `cache-and-network`,
  })

  // Set that repository object in our form state, if it hasn't been set
  if (repository && !formState.repo) {
    dispatch({
      type: RepoFormActionType.SelectRepo,
      payload: repository as Repository,
    })
    const nameWithOwner = repository?.nameWithOwner

    if (!nameWithOwner) {
      return null
    }

    loadBranches({
      variables: {
        nameWithOwner,
        provider,
      },
    })
  }

  /*
    Loading and Error States
  */

  if (loading && sourceOrganizations.length === 0) {
    return (
      <Loading
        delay={1000}
        message={importSiteText.messages.loadingSourceProviderTeams}
        bufferSize="padded"
      />
    )
  }

  if (error) {
    return (
      <NarrowSingleColumn>
        <ErrorAlert>{error.message}</ErrorAlert>
      </NarrowSingleColumn>
    )
  }

  /*
    Possibly don't need this path - need to determine whether there are any 
    cases where you can reach this path without an auth'd provider
  */
  if (sourceOrganizations.length === 0) {
    return (
      <NarrowSingleColumn>
        <NoInstallations
          hasClosedWindow={hasClosedWindow}
          popInstallationWindow={popInstallationWindow}
          provider={provider}
          organizationId={organizationId}
          vcsConfigId={vcsConfigId}
        />
      </NarrowSingleColumn>
    )
  }

  /*
    End Loading and Error States
  */

  // Form submission callback
  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault()
    setError(null)
    // verify sitename uniqueness
    const isValid = await verifySiteNameUniqueness({
      name: formState.siteName,
      sourceOrganizationId: selectedInstallation?.id,
      organizationId: organizationId,
      sourceControlProvider: provider,
    })

    if (isValid) {
      // Create site
      trackAction({
        eventType: `TRACK_EVENT`,
        name: `Create Site - Select repo`,
        uiSource: `Site setup wizard`,
      })
      await createSite({
        organizationId: organizationId,
        enterpriseVCSConfigId: vcsConfigId,
        provider,
        nameWithOwner: formState.repo?.nameWithOwner || "",
        directoryPath: formState.directoryPath,
        branch: formState.branch,
        selectedInstallationId: selectedInstallation?.id || "",
        successAction: onSiteCreated,
        publicName: formState.siteName,
      })
    }
  }

  // Vars for displaying branch and sitename inputs
  const disableSubmitButton =
    !formState.siteName ||
    !formState.repo ||
    !formState.branch ||
    !isCurrentStep

  return createSiteLoading ? (
    <Loading
      message={importSiteText.messages.creatingSite}
      bufferSize="padded"
    />
  ) : (
    <StyledForm onSubmit={onSubmit}>
      {errorAlert && (
        <React.Fragment>
          {errorAlert}
          <Spacer size={9} />
        </React.Fragment>
      )}
      {!formState.isBranchInitialized && (
        <Loading
          delay={1000}
          bufferSize="padded"
          message={importSiteText.messages.loadingRepositoryBranches}
        />
      )}

      {formState.repo && formState.isBranchInitialized && (
        <React.Fragment>
          <InputFieldBlock
            id="site-name"
            label={newImportSiteText.labels.siteNameField}
            error={validationError || serverError}
            value={formState.siteName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              dispatch({
                type: RepoFormActionType.ChangeSiteName,
                payload: e.target.value,
              })
              clearError()
            }}
            onFocus={() =>
              dispatch({
                type: RepoFormActionType.TouchSiteName,
              })
            }
          />
          <Spacer size={7} />
        </React.Fragment>
      )}

      {formState.repo && formState.isBranchInitialized && (
        <div role="group" aria-labelledby="branchAndPath__title">
          <div css={formSectionCss}>
            <FieldWrapper size={4}>
              <BranchField
                title={newImportSiteText.labels.baseBranchField}
                nameWithOwner={formState.repo?.nameWithOwner}
                selectedBranch={formState.branch}
                onSelect={async (value: string) => {
                  let suggestedSiteName = getDefaultSiteName(repoName, value)
                  /*
                      As you'd expect, this makes the select box sluggish. 
                      TODO: determine whether we want to check for uniqueness 
                      when changing branches
                    */
                  const isUniqueName = await verifySiteNameUniqueness({
                    name: suggestedSiteName,
                    sourceOrganizationId: selectedInstallation?.id,
                    organizationId: organizationId,
                    sourceControlProvider: provider,
                  })
                  clearError()

                  if (!isUniqueName) {
                    suggestedSiteName = getUniqueSiteName(suggestedSiteName)
                  }
                  dispatch({
                    type: RepoFormActionType.SelectBranch,
                    payload: { branch: value, siteName: suggestedSiteName },
                  })
                }}
                provider={provider}
              />
            </FieldWrapper>
            <FieldWrapper size={6} withLeftSpacing>
              <InputFieldBlock
                id="directoryPath"
                name="directoryPath"
                label={newImportSiteText.labels.baseDirectoryField}
                hint={importSiteText.messages.baseDirectoryHint}
                placeholder="/"
                value={formState.directoryPath}
                onChange={e => {
                  dispatch({
                    type: RepoFormActionType.ChangeDirectoryPath,
                    payload: e.target.value,
                  })
                  clearError()
                }}
              />
            </FieldWrapper>
          </div>
        </div>
      )}
      <Button
        disabled={disableSubmitButton}
        loading={loadingSiteNameUniqueness}
        rightIcon={<MdArrowDownward />}
        type="submit"
      >
        {uiText.actions.next}
      </Button>
    </StyledForm>
  )
}
